import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useQueryParams } from "raviger";
import { useFieldArray, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import * as z from "zod";

import Callout from "@/CAREUI/display/Callout";
import WeekdayCheckbox, {
  DayOfWeek,
} from "@/CAREUI/interactive/WeekdayCheckbox";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import useBreakpoints from "@/hooks/useBreakpoints";

import CareIcon from "@/CAREUI/icons/CareIcon";
import mutate from "@/Utils/request/mutate";
import { Time } from "@/Utils/types";
import { dateQueryString } from "@/Utils/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { useOpenChangeWithConfirmation } from "@/hooks/useOpenChangeWithConfirmation";
import {
  calculateIntegerSlotDuration,
  getDurationInMinutes,
  isExactSlotDuration,
} from "@/pages/Scheduling/utils";
import {
  AvailabilityRecurrenceType,
  SchedulableResourceType,
  ScheduleAvailabilityCreateRequest,
} from "@/types/scheduling/schedule";
import scheduleApis from "@/types/scheduling/scheduleApi";

interface Props {
  facilityId: string;
  resourceType: SchedulableResourceType;
  resourceId: string;
  trigger?: React.ReactNode;
}

type QueryParams = {
  sheet?: "create_template" | null;
};

export default function CreateScheduleTemplateSheet({
  facilityId,
  resourceType,
  resourceId,
  trigger,
}: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Voluntarily masking the setQParams function to merge with other query params if any (since path is not unique within the user availability tab)
  const [qParams, _setQParams] = useQueryParams<QueryParams>();
  const setQParams = (p: QueryParams) => _setQParams(p, { overwrite: false });

  const weekdayFormat = useBreakpoints({
    default: "alphabet",
    md: "short",
  } as const);

  const formSchema = z
    .object({
      name: z.string().trim().min(1, t("field_required")),
      valid_from: z
        .date({ required_error: t("field_required") })
        .min(dayjs().startOf("day").toDate(), {
          message: t("schedule_creation_for_past_validation_error"),
        }),
      valid_to: z.date({ required_error: t("field_required") }),
      is_public: z.boolean(),

      recurrence: z.nativeEnum(AvailabilityRecurrenceType),
      weekdays: z
        .array(z.number() as unknown as z.ZodType<DayOfWeek>)
        .optional(),
      day_of_month: z
        .number()
        .int(t("schedule_day_of_month_whole_number"))
        .min(1, t("number_min_error", { min: 1 }))
        .max(31, t("schedule_day_of_month_range_error"))
        .optional(),
      availabilities: z
        .array(
          z
            .discriminatedUnion("slot_type", [
              // Schema for appointment type
              z.object({
                slot_type: z.literal("appointment"),
                name: z.string().trim().min(1, t("field_required")),
                reason: z.string().trim(),
                start_time: z
                  .string({ required_error: t("field_required") })
                  .min(1, t("field_required")) as z.ZodType<Time | undefined>,
                end_time: z
                  .string({ required_error: t("field_required") })
                  .min(1, t("field_required")) as z.ZodType<Time | undefined>,
                slot_size_in_minutes: z
                  .number()
                  .int(t("schedule_slot_duration_must_be_whole_number"))
                  .min(1, t("number_min_error", { min: 1 }))
                  .optional(),
                tokens_per_slot: z
                  .union([
                    z.number().min(1, t("number_min_error", { min: 1 })),
                    z.undefined(),
                  ])
                  .refine((val) => val !== undefined, {
                    message: t("field_required"),
                  }),
                is_auto_fill: z.boolean().optional(),
                num_of_slots: z
                  .union([
                    z
                      .number()
                      .int(t("schedule_num_slots_must_be_whole_number"))
                      .min(1, t("number_min_error", { min: 1 })),
                    z.undefined(),
                  ])
                  .refine((val) => val !== undefined, {
                    message: t("field_required"),
                  }),
              }),
              // Schema for open and closed types
              z.object({
                slot_type: z.enum(["open", "closed"]),
                name: z.string().trim().min(1, t("field_required")),
                reason: z.string().trim(),
                start_time: z
                  .string({ required_error: t("field_required") })
                  .min(1, t("field_required")) as unknown as z.ZodType<Time>,
                end_time: z
                  .string({ required_error: t("field_required") })
                  .min(1, t("field_required")) as unknown as z.ZodType<Time>,
                slot_size_in_minutes: z.literal(null),
                tokens_per_slot: z.literal(null),
              }),
            ])
            .refine(
              (data) => {
                // Validate each availability's time range
                const startTime = dayjs(data.start_time, "HH:mm");
                const endTime = dayjs(data.end_time, "HH:mm");
                return startTime.isBefore(endTime);
              },
              {
                message: t("start_time_must_be_before_end_time"),
                path: ["start_time"],
              },
            ),
        )
        .min(1, t("schedule_sessions_min_error"))
        .superRefine((availabilities, context) => {
          availabilities.forEach((availability, index) => {
            if (
              availability.slot_type !== "appointment" ||
              !availability.start_time ||
              !availability.end_time
            ) {
              return;
            }

            if (availability.is_auto_fill) {
              const slotDuration = calculateIntegerSlotDuration(
                availability.start_time,
                availability.end_time,
                availability.num_of_slots ?? 0,
              );

              if (slotDuration === null) {
                context.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [index, "slot_size_in_minutes"],
                  message: t(
                    "schedule_availability_duration_not_evenly_divisible",
                  ),
                });
                return;
              }

              if (
                availability.slot_size_in_minutes !== undefined &&
                availability.slot_size_in_minutes !== slotDuration
              ) {
                context.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: [index, "slot_size_in_minutes"],
                  message: t(
                    "schedule_slot_duration_must_evenly_divide_availability",
                  ),
                });
              }

              return;
            }

            if (availability.slot_size_in_minutes === undefined) {
              context.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, "slot_size_in_minutes"],
                message: t("field_required"),
              });
              return;
            }

            if (
              !isExactSlotDuration(
                availability.start_time,
                availability.end_time,
                availability.slot_size_in_minutes,
              )
            ) {
              context.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, "slot_size_in_minutes"],
                message: t(
                  "schedule_slot_duration_must_evenly_divide_availability",
                ),
              });
            }
          });
        }),
    })
    .refine(
      (data) => !dayjs(data.valid_to).isBefore(dayjs(data.valid_from), "day"),
      {
        path: ["valid_to"],
        message: t("to_date_equal_or_after_from_date"),
      },
    )
    .superRefine((data, context) => {
      if (
        data.recurrence === AvailabilityRecurrenceType.Weekly &&
        (!data.weekdays || data.weekdays.length === 0)
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["weekdays"],
          message: t("schedule_weekdays_min_error"),
        });
      }
      if (
        data.recurrence === AvailabilityRecurrenceType.Monthly &&
        data.day_of_month === undefined
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["day_of_month"],
          message: t("field_required"),
        });
      }
    });

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      valid_from: undefined,
      valid_to: undefined,
      weekdays: [],
      recurrence: AvailabilityRecurrenceType.Weekly,
      day_of_month: undefined,
      is_public: false,
      availabilities: [
        {
          name: "",
          slot_type: "appointment" as const,
          reason: "",
          start_time: undefined,
          end_time: undefined,
          tokens_per_slot: undefined,
          slot_size_in_minutes: undefined,
          is_auto_fill: false,
          num_of_slots: 1,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "availabilities",
  });

  const { mutate: createTemplate, isPending } = useMutation({
    mutationFn: mutate(scheduleApis.templates.create, {
      pathParams: { facilityId },
    }),
    onSuccess: () => {
      toast.success(t("schedule_template_created_successfully"));
      setQParams({ sheet: null });
      form.reset();
      queryClient.invalidateQueries({
        queryKey: ["schedule", facilityId, { resourceType, resourceId }],
      });
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const availabilityRules =
      values.recurrence === AvailabilityRecurrenceType.Monthly
        ? [
            {
              recurrence: AvailabilityRecurrenceType.Monthly,
              day_of_month: values.day_of_month,
              start_time: values.availabilities[0]?.start_time,
              end_time: values.availabilities[0]?.end_time,
            },
          ]
        : (values.weekdays ?? []).map((day) => ({
            recurrence: AvailabilityRecurrenceType.Weekly,
            day_of_week: day,
            start_time: undefined,
            end_time: undefined,
          }));

    createTemplate({
      valid_from: dateQueryString(values.valid_from),
      valid_to: dateQueryString(values.valid_to),
      name: values.name,
      resource_type: resourceType,
      resource_id: resourceId,
      is_public: values.is_public,
      availabilities: values.availabilities.map(
        (availability) =>
          ({
            name: availability.name,
            slot_type: availability.slot_type,
            slot_size_in_minutes: availability.slot_size_in_minutes,
            tokens_per_slot: availability.tokens_per_slot,
            reason: availability.reason,
            availability: availabilityRules.map((rule) => ({
              ...rule,
              start_time: availability.start_time,
              end_time: availability.end_time,
            })),
          }) as ScheduleAvailabilityCreateRequest,
      ),
    });
  }

  const timeAllocationCallout = (index: number) => {
    const startTime = form.watch(`availabilities.${index}.start_time`);
    const endTime = form.watch(`availabilities.${index}.end_time`);
    const slotSizeInMinutes = form.watch(
      `availabilities.${index}.slot_size_in_minutes`,
    );

    if (!startTime || !endTime || !slotSizeInMinutes) {
      return null;
    }

    const durationInMinutes = getDurationInMinutes(startTime, endTime);

    if (
      durationInMinutes === null ||
      !isExactSlotDuration(startTime, endTime, slotSizeInMinutes)
    ) {
      return null;
    }

    return (
      <Callout variant="alert" badge="Info">
        <Trans
          i18nKey="schedule_slots_allocation_callout"
          values={{
            slots: durationInMinutes / slotSizeInMinutes,
            slot_duration: slotSizeInMinutes,
          }}
        />
      </Callout>
    );
  };

  const updateSlotDuration = (index: number) => {
    const isAutoFill = form.watch(`availabilities.${index}.is_auto_fill`);
    if (isAutoFill) {
      const start = form.watch(`availabilities.${index}.start_time`);
      const end = form.watch(`availabilities.${index}.end_time`);
      const numOfSlots = form.watch(`availabilities.${index}.num_of_slots`);
      if (!start || !end || !numOfSlots) return;
      const duration = calculateIntegerSlotDuration(
        start,
        end,
        numOfSlots ?? 0,
      );
      form.setValue(
        `availabilities.${index}.slot_size_in_minutes`,
        duration ?? undefined,
        { shouldDirty: true, shouldValidate: true },
      );
      if (duration === null) {
        form.setError(`availabilities.${index}.slot_size_in_minutes`, {
          type: "manual",
          message: t("schedule_availability_duration_not_evenly_divisible"),
        });
      } else {
        form.clearErrors(`availabilities.${index}.slot_size_in_minutes`);
      }
    }
  };

  const isFormDirty = form.formState.isDirty;

  const handleClose = useOpenChangeWithConfirmation({
    isDirty: isFormDirty,
  });

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setQParams({ sheet: "create_template" });
      return;
    }
    const canProceed = handleClose(open);
    if (!canProceed) {
      return;
    }
    setQParams({ sheet: null });
    form.reset();
  };

  return (
    <Sheet
      open={qParams.sheet === "create_template"}
      onOpenChange={handleOpenChange}
    >
      <SheetTrigger asChild>
        {trigger ?? (
          <Button variant="primary" disabled={isPending}>
            {t("create_template")}
          </Button>
        )}
      </SheetTrigger>
      <SheetContent className="flex min-w-full flex-col bg-gray-100 sm:min-w-fit">
        <SheetHeader>
          <SheetTitle>{t("create_schedule_template")}</SheetTitle>
          <SheetDescription className="sr-only">
            {t("create_schedule_template")}
          </SheetDescription>
        </SheetHeader>

        <div className="-mx-6 mb-16 overflow-auto px-6 pb-16 pt-6">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="min-w-0 max-w-full space-y-6"
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel aria-required>
                      {t("schedule_template_name")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("schedule_template_name_placeholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="valid_from"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel aria-required>{t("valid_from")}</FormLabel>
                      <DatePicker
                        date={field.value}
                        onChange={(date) => field.onChange(date)}
                        disabled={(date) =>
                          dayjs(date).isBefore(dayjs(), "day")
                        }
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="valid_to"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel aria-required>{t("valid_to")}</FormLabel>
                      <DatePicker
                        date={field.value}
                        onChange={(date) => field.onChange(date)}
                        disabled={(date) =>
                          dayjs(date).isBefore(dayjs(), "day")
                        }
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-3">
                <FormField
                  control={form.control}
                  name="recurrence"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel aria-required>{t("recurrence")}</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={AvailabilityRecurrenceType.Weekly}>
                            {t("weekly_schedule")}
                          </SelectItem>
                          <SelectItem
                            value={AvailabilityRecurrenceType.Monthly}
                          >
                            {t("monthly_schedule")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {form.watch("recurrence") ===
                AvailabilityRecurrenceType.Monthly ? (
                  <FormField
                    control={form.control}
                    name="day_of_month"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel aria-required>
                          {t("schedule_day_of_month")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            max={31}
                            value={field.value ?? ""}
                            onChange={(event) => {
                              const value = event.target.valueAsNumber;
                              field.onChange(
                                Number.isNaN(value) ? undefined : value,
                              );
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <FormField
                    control={form.control}
                    name="weekdays"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel aria-required>
                          {t("schedule_weekdays")}
                        </FormLabel>
                        <span className="block text-sm">
                          {t("schedule_weekdays_description")}
                        </span>
                        <FormControl>
                          <WeekdayCheckbox
                            value={field.value ?? []}
                            onChange={field.onChange}
                            format={weekdayFormat}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <FormField
                control={form.control}
                name="is_public"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-gray-200 p-4 bg-white">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none -mt-1">
                      <FormLabel className="text-base">
                        {t("make_template_public")}
                      </FormLabel>
                      <p className="text-sm text-gray-500">
                        {t("make_template_public_description")}
                      </p>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="space-y-4">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="flex min-w-0 max-w-full flex-col rounded-lg bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-center justify-between pb-6">
                      <div className="flex items-center gap-2">
                        <CareIcon
                          icon="l-clock"
                          className="text-lg text-blue-600"
                        />
                        <span className="font-semibold">
                          {form.watch(`availabilities.${index}.name`)}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="text-gray-600 hover:text-gray-900"
                        onClick={() => remove(index)}
                      >
                        <TrashIcon className="text-base" />
                        <span className="ml-2">{t("remove")}</span>
                      </Button>
                    </div>

                    <div className="flex min-w-0 max-w-full flex-col gap-x-6 gap-y-4">
                      <div className="items-stretch">
                        <FormField
                          control={form.control}
                          name={`availabilities.${index}.name`}
                          render={({ field }) => (
                            <FormItem className="col-span-2">
                              <FormLabel aria-required>
                                {t("session_title")}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  placeholder={t("session_title_placeholder")}
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* <FormField
                        control={form.control}
                        name={`availabilities.${index}.slot_type`}
                        render={({ field }) => (
                          <FormItem className="col-span-2 md:col-span-1">
                            <FormLabel aria-required>{t("session_type")}</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue asChild>
                                    <span>
                                      {t(
                                        `SCHEDULE_AVAILABILITY_TYPE__${field.value}`,
                                      )}
                                    </span>
                                  </SelectValue>
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {["appointment", "open", "closed"].map(
                                  (type) => (
                                    <SelectItem key={type} value={type}>
                                      <p>
                                        {t(
                                          `SCHEDULE_AVAILABILITY_TYPE__${type}`,
                                        )}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {t(
                                          `SCHEDULE_AVAILABILITY_TYPE_DESCRIPTION__${type}`,
                                        )}
                                      </p>
                                    </SelectItem>
                                  ),
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      /> */}
                      <div className="flex flex-wrap">
                        <FormField
                          control={form.control}
                          name={`availabilities.${index}.start_time`}
                          render={({ field }) => (
                            <FormItem className="flex flex-col w-full">
                              <FormLabel aria-required>
                                {t("start_time")}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="time"
                                  {...field}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    updateSlotDuration(index);
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name={`availabilities.${index}.end_time`}
                          render={({ field }) => (
                            <FormItem className="flex flex-col w-full mt-2">
                              <FormLabel aria-required>
                                {t("end_time")}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="time"
                                  {...field}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    updateSlotDuration(index);
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {form.watch(`availabilities.${index}.slot_type`) ===
                        "appointment" && (
                        <>
                          <div className="flex min-w-0 max-w-full flex-wrap gap-2 pt-2 mt-0">
                            <div className="w-full gap-x-2 grid grid-cols-[auto_1fr_auto] mb-2 bg-gray-50 p-3 rounded-lg">
                              <CareIcon
                                icon="l-bolt"
                                className="text-lg text-blue-600"
                              />
                              <Label
                                htmlFor={`auto-fill-${index}`}
                                className="text-sm font-medium cursor-pointer col-start-2"
                              >
                                {t("auto_fill_slot_duration")}
                              </Label>
                              <FormField
                                control={form.control}
                                name={`availabilities.${index}.is_auto_fill`}
                                render={({ field }) => (
                                  <Switch
                                    className="col-start-3"
                                    id={`auto-fill-${index}`}
                                    checked={field.value}
                                    onCheckedChange={(checked) => {
                                      field.onChange(checked);
                                      if (checked) {
                                        updateSlotDuration(index);
                                      }
                                    }}
                                  />
                                )}
                              />
                              {form.watch(
                                `availabilities.${index}.is_auto_fill`,
                              ) && (
                                <div className="row-start-2 col-start-2 col-span-2">
                                  <FormField
                                    control={form.control}
                                    name={`availabilities.${index}.num_of_slots`}
                                    render={({ field }) => (
                                      <FormItem className="flex flex-col mt-2 space-y-0">
                                        <Label className="text-sm font-light">
                                          {t("number_of_slots")}
                                        </Label>
                                        <FormControl>
                                          <Input
                                            type="number"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            min={1}
                                            step={1}
                                            defaultValue={1}
                                            {...field}
                                            className="shadow-none"
                                            onChange={(e) => {
                                              const value =
                                                e.target.valueAsNumber;
                                              field.onChange(
                                                isNaN(value)
                                                  ? undefined
                                                  : value,
                                              );
                                              updateSlotDuration(index);
                                            }}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>
                              )}
                            </div>

                            <FormField
                              control={form.control}
                              name={`availabilities.${index}.slot_size_in_minutes`}
                              render={({ field }) => (
                                <FormItem className="w-0 min-w-0 flex-1">
                                  <FormLabel
                                    aria-required
                                    className="whitespace-nowrap"
                                  >
                                    {t("schedule_slot_size_label")}
                                  </FormLabel>

                                  <FormControl>
                                    <Input
                                      type="number"
                                      inputMode="numeric"
                                      pattern="[0-9]*"
                                      min={1}
                                      step={1}
                                      placeholder="e.g. 10"
                                      {...field}
                                      value={field.value ?? ""}
                                      onChange={(e) => {
                                        const value = e.target.valueAsNumber;
                                        field.onChange(
                                          isNaN(value) ? undefined : value,
                                        );
                                      }}
                                      disabled={form.watch(
                                        `availabilities.${index}.is_auto_fill`,
                                      )}
                                    />
                                  </FormControl>

                                  <FormMessage className="min-w-0 max-w-full break-words" />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name={`availabilities.${index}.tokens_per_slot`}
                              render={({ field }) => (
                                <FormItem className="min-w-0 max-w-full flex flex-col flex-1">
                                  <FormLabel
                                    aria-required
                                    className="whitespace-nowrap"
                                  >
                                    {t("patients_per_slot")}
                                  </FormLabel>
                                  <FormControl>
                                    <Input
                                      type="number"
                                      inputMode="numeric"
                                      pattern="[0-9]*"
                                      min={1}
                                      placeholder="e.g. 1"
                                      {...field}
                                      value={field.value ?? ""}
                                      onChange={(e) => {
                                        const value = e.target.valueAsNumber;
                                        field.onChange(
                                          isNaN(value) ? undefined : value,
                                        );
                                      }}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <div className="col-span-2 mb-2">
                            {timeAllocationCallout(index)}
                          </div>
                        </>
                      )}
                    </div>

                    <div>
                      <FormField
                        control={form.control}
                        name={`availabilities.${index}.reason`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("remarks")}</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder={t("remarks_placeholder")}
                                className="resize-none"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                ))}
                {form.formState.errors.availabilities && (
                  <FormMessage>
                    {form.formState.errors.availabilities.root?.message}
                  </FormMessage>
                )}
              </div>

              <Button
                type="button"
                variant="outline_primary"
                onClick={() => {
                  append({
                    name: "",
                    slot_type: "appointment",
                    reason: "",
                    start_time: undefined,
                    end_time: undefined,
                    tokens_per_slot: undefined,
                    slot_size_in_minutes: undefined,
                    is_auto_fill: false,
                    num_of_slots: 1,
                  });
                }}
              >
                <PlusIcon className="text-lg" />
                <span>{t("add_another_session")}</span>
              </Button>

              <SheetFooter className="absolute inset-x-0 bottom-0 border-t border-gray-200 bg-white p-6">
                <SheetClose asChild>
                  <Button variant="outline" type="button" disabled={isPending}>
                    {t("cancel")}
                  </Button>
                </SheetClose>

                <Button variant="primary" type="submit" disabled={isPending}>
                  {isPending ? t("saving") : t("save")}
                </Button>
              </SheetFooter>
            </form>
          </Form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
